'use client';

import type { ForwardedRef } from 'react';
import React, { useRef } from 'react';
import type { DraggableItemResult, DroppableItemResult } from 'react-aria';
import { mergeProps } from 'react-aria';
import type { Key, ListState, Node } from 'react-stately';
import { useAnimateItem } from 'components/Collections/animations';
import { useDragAndDropContext } from 'components/Collections/dnd/DragAndDropProvider';
import { DropIndicator } from 'components/Collections/dnd/DropIndicator';
import { useLink } from 'components/RouterProvider';
import { Text } from 'components/Text';
import { getSyntheticLinkProps, mergeRefs } from '@react-aria/utils';
import styles from './Carousel.module.css';
import { CarouselItemCheckbox } from './CarouselItemCheckbox';
import { useCarouselListItem } from './useCarouselItem';

type CarouselListItemProps<T> = {
  item: Node<T>;
  shouldRender: boolean;
  state: ListState<T>;
  onAction: (key: Key) => void;
  isActivated?: boolean;
  index: number;
  totalCount: number;
  x: number;
  y: number;
  width: number;
  height: number;
  itemGap: number;
};

function CarouselListItem<T>(
  {
    item,
    shouldRender,
    state,
    onAction,
    isActivated,
    index,
    totalCount,
    x,
    y,
    width,
    height,
    itemGap,
  }: CarouselListItemProps<T>,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const animatedContainerRef = useRef<HTMLDivElement>(undefined);

  useAnimateItem(animatedContainerRef, { x, y });

  const ref = useRef<HTMLDivElement>(undefined);
  const { LinkComponent, linkProps } = useLink(item.props);

  const { as: Component, textValue, variant, ...other } = item.props;

  const selectionMode = state.selectionManager.selectionMode;

  const {
    isSelected,
    isFocusVisible,
    isHovered,
    gridCellProps,
    carouselItemProps,
  // @ts-expect-error FIXME(strictNullChecks)
  } = useCarouselListItem(ref, state, {
    key: item.key,
    isActivated,
    onAction,
  });

  /**
   * Drag and drop support
   */

  const { dragAndDropHooks, dragState, dropState } = useDragAndDropContext();

  let dragItem: DraggableItemResult;

  if (dragState) {
    // @ts-expect-error FIXME(strictNullChecks)
    dragItem = dragAndDropHooks.useDraggableItem(
      {
        key: item.key,
      },
      dragState
    );
  }

  let dropItem: DroppableItemResult;

  if (dropState) {
    // @ts-expect-error FIXME(strictNullChecks)
    dropItem = dragAndDropHooks.useDroppableItem(
      { target: { type: 'item', key: item.key, dropPosition: 'on' } },
      dropState,
      // @ts-expect-error FIXME(strictNullChecks)
      ref
    );
  }

  const isDragging = dragState?.isDragging(item.key);

  const isLink = state.selectionManager.isLink(item.key);

  const ItemComponent = Component || 'div';

  return (
    <>
      {dropState && (
        <DropIndicator
          marginX={0}
          orientation="horizontal"
          style={{ height: `${height}px` }}
          target={{ type: 'item', key: item.key, dropPosition: 'before' }}
          x={x - itemGap / 2}
          y={y}
        />
      )}
      {/* @ts-expect-error FIXME(strictNullChecks) */}
      <div ref={animatedContainerRef} className={styles.animationContainer}>
        <ItemComponent
          ref={mergeRefs(ref, forwardedRef)}
          aria-label={textValue}
          data-key={item.key}
          data-placeholder={!shouldRender}
          {...mergeProps(
            { className: styles.carouselItem },
            carouselItemProps,
            other,
            // @ts-expect-error FIXME(strictNullChecks)
            dragItem?.dragProps,
            // @ts-expect-error FIXME(strictNullChecks)
            dropItem?.dropProps,
            getSyntheticLinkProps(item.props),
            {
              'data-selected': isSelected || undefined,
              'data-activated': isActivated || undefined,
              'data-focused': isFocusVisible || undefined,
              'data-hovered': isHovered || undefined,
              'data-has-action': !!onAction || undefined,
              'data-dragging': isDragging || undefined,
              // @ts-expect-error FIXME(strictNullChecks)
              'data-drop-target': dropItem?.isDropTarget || undefined,
              'data-variant': variant,
              style: {
                '--item-width': `${width}px`,
                '--item-height': `${height}px`,
              },
            }
          )}
        >
          <div style={{ display: 'contents' }} {...gridCellProps}>
            {shouldRender ? (
              <>
                {item.rendered}
                {isLink && (
                  // @ts-expect-error FIXME(strictNullChecks)
                  <LinkComponent
                    {...linkProps}
                    aria-hidden="true"
                    className={styles.backgroundLink}
                    data-react-aria-prevent-focus="true"
                    tabIndex={-1}
                  />
                )}
                {variant !== 'unstyled' && (
                  <>
                    {selectionMode !== 'none' && (
                      <CarouselItemCheckbox item={item} state={state} />
                    )}
                    <div
                      className={styles.countBadge}
                      data-hovered={isHovered || isFocusVisible || undefined}
                    >
                      {index + 1} / {totalCount}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>
        </ItemComponent>
        {item.textValue && variant !== 'unstyled' && (
          <div
            className={styles.textContainer}
            data-hovered={
              ((isHovered || isFocusVisible) && !isDragging) || undefined
            }
          >
            <Text
              color="primary"
              fontWeight="regular"
              truncate={{ type: 'middle', padding: 7 }}
              variant="secondary"
            >
              {item.textValue}
            </Text>
          </div>
        )}
      </div>
    </>
  );
}

const _CarouselListItem = React.forwardRef(CarouselListItem);
_CarouselListItem.displayName = 'CarouselListItem';
export { _CarouselListItem as CarouselListItem };
